import {
  useState,
  useEffect,
  useCallback,
  ChangeEvent,
  useMemo,
  useRef,
} from 'react'
import {
  CellContext,
  ColumnDef,
  Row as RowType,
  Column as ColumnType,
} from '@tanstack/react-table'

import { TableCell } from '@/components/ui/table'
import {
  Table,
  TableColumn,
  TableRow,
  TemplateAttribute,
  useCreateTable,
  useUpdateTable,
} from '@/services/hooks/template_attributes'
import type { FlattenedDocumentPage } from '@/lib/api/client'
import {
  EditableTable,
  createNewColumn,
  getHighlightedCellStyle,
  type EditableTableMeta,
} from './EditableTable'
import { useDocumentState } from '@/state/document'
import { useAssemblyTreeQuery } from '@/pages/CADPage/queries'
import { useUpdateCadVersionMutation } from '@/pages/CADPage/queries'
import { useCADQuery } from '@/services/queries/cads'
import { useFullPartsBOM, useStepBOM } from './OperationDocument/hooks/useBOM'
import { useToast } from '@/components/ui/use-toast'
import { Textarea } from '@/components/ui/textarea'
import { useCadVersionMetadata } from '@/services/queries/cad_versions'

export type PartsTableData = {
  partName: any
  quantity: number | string
  [key: string]: any
}

const EMPTY_CUSTOM_ROW_KEY = '__emptyCustomRow'

type PartsTableProps = {
  template?: TemplateAttribute<Table>
  isReadOnly?: boolean
  documentPage: FlattenedDocumentPage
  dataType: 'parts-table'
  onNudgeStart?: (showGrid: boolean) => void
  onNudgeEnd?: (hideGrid: boolean) => void
}

export const PartsTable = ({
  template,
  isReadOnly = false,
  documentPage,
  dataType,
  onNudgeStart,
  onNudgeEnd,
}: PartsTableProps) => {
  const draggableId = useMemo(
    () => template?.id || dataType,
    [template?.id, dataType],
  )

  const partsInOperation = useStepBOM({
    documentPageId: documentPage.id,
  })

  const fullBom = useFullPartsBOM()

  const { data: cadData } = useCADQuery()

  const cadVersionId = useMemo(
    () => cadData?.version?.id as string,
    [cadData?.version],
  )

  const setSelectedElement = useDocumentState(
    (state) => state.setSelectedElement,
  )

  const selectTable = useCallback(() => {
    setSelectedElement({
      documentPageId: documentPage.id as string,
      id: draggableId,
      isRemovable: false,
      type: dataType,
    })
  }, [documentPage.id, draggableId, dataType, setSelectedElement])

  const initialColumns = useMemo<Record<string, ColumnDef<PartsTableData>>>(
    () => ({
      id: {
        accessorKey: 'id',
        header: 'Id',
        size: 35,
        enableResizing: false,
        cell: ({ column, row, table }) => {
          const rowIndex = row.index
          const meta = table.options.meta as EditableTableMeta | undefined
          return (
            <TableCell
              className="border-x border-solid border-gray-200 border-collapse py-0 relative bg-white/70"
              style={{
                position: 'relative',
                width: column.getSize(),
                boxShadow: getHighlightedCellStyle({
                  meta,
                  rowIndex,
                  columnId: 'id',
                }),
              }}
              tabIndex={0}
              onFocus={() => {
                meta?.highlightCell({
                  columnId: 'id',
                  rowIndex,
                  source: 'focus',
                })
              }}
              onBlur={() =>
                meta?.clearHighlightedCell('focus', {
                  columnId: 'id',
                  rowIndex,
                })
              }
              data-id-column="id"
              data-id-row={rowIndex}
            >
              <span
                className="flex border border-black shadow-none m-0 text-xs h-4 w-4 rounded-full left-0 right-0 m-auto justify-center items-center text-xs p-2"
                data-testid="editable-table-char-code-id"
                data-id-column="id"
                data-id-row={rowIndex}
              >
                {String.fromCharCode(65 + row.index)}
              </span>
            </TableCell>
          )
        },
      },
      partName: {
        accessorKey: 'partName',
        header: 'Part Name',
        size: 340,
        enableResizing: true,
        cell: ({ getValue, table, column, row }) => {
          const meta = table.options.meta as EditableTableMeta | undefined

          return isReadOnly ? (
            <TableCell
              className="items-center border-x border-solid border-gray-200 border-collapse text-xs py-1 bg-white/70"
              style={{
                position: 'relative',
                width: column.getSize(),
                boxShadow: getHighlightedCellStyle({
                  meta,
                  rowIndex: row.index,
                  columnId: 'partName',
                }),
              }}
              tabIndex={0}
              onFocus={() => {
                meta?.highlightCell({
                  columnId: 'partName',
                  rowIndex: row.index,
                  source: 'focus',
                })
              }}
              onBlur={() =>
                meta?.clearHighlightedCell('focus', {
                  columnId: 'partName',
                  rowIndex: row.index,
                })
              }
              data-id-column="partName"
              data-id-row={row.index}
            >
              {getValue<any>().partName}
            </TableCell>
          ) : (
            <NameCell
              documentPageId={documentPage.id as string}
              getValue={getValue}
              selectTable={selectTable}
              tableId={(table.options.meta as any).tableId}
              tableMeta={meta}
              width={column.getSize()}
              row={row}
              column={column}
            />
          )
        },
      },
      quantity: {
        accessorKey: 'quantity',
        header: 'Qty',
        size: 60,
        enableResizing: true,
        cell: ({ getValue, column, row, table }) => {
          const accessor = (column.columnDef as any).accessorKey as string
          const rowIndex = row.index
          const meta = table.options.meta as EditableTableMeta | undefined
          return (
            <TableCell
              className="items-center border-x border-solid border-gray-200 border-collapse text-xs py-1 bg-white/70"
              style={{
                position: 'relative',
                width: column.getSize(),
                boxShadow: getHighlightedCellStyle({
                  meta,
                  rowIndex,
                  columnId: accessor,
                }),
              }}
              tabIndex={0}
              onFocus={() => {
                meta?.highlightCell({
                  columnId: accessor,
                  rowIndex,
                  source: 'focus',
                })
              }}
              onBlur={() =>
                meta?.clearHighlightedCell('focus', {
                  columnId: accessor,
                  rowIndex,
                })
              }
              data-id-column={accessor}
              data-id-row={rowIndex}
            >
              <span data-id-column={accessor} data-id-row={rowIndex}>
                {getValue<string>()}
              </span>
            </TableCell>
          )
        },
      },
    }),
    [documentPage.id, isReadOnly, selectTable],
  )

  const { data: metadata } = useCadVersionMetadata({ cadVersionId })

  const [columnsDef, setColumnsDef] = useState<
    Array<ColumnDef<PartsTableData>>
  >([])
  const [columnsData, setColumnsData] = useState({})

  const metadataKeys = useMemo(() => metadata?.keys || [], [metadata])
  const columnOptions = useMemo(
    () =>
      metadataKeys.reduce(
        (accum, key) => {
          accum[key] = key
          return accum
        },
        {
          id: 'Id',
          partName: 'Part Name',
          quantity: 'Qty',
        } as { [accessor: string]: string },
      ),
    [metadataKeys],
  )

  const metadataByAssemblyGroupIds = useMemo(
    () => metadata?.assembly_group_ids || {},
    [metadata],
  )

  const initialTableData = useMemo(
    () =>
      partsInOperation.map<PartsTableData>((part, i) => {
        const uuid = part.UUIDs?.[0]
        let partMetadata = {}
        if (uuid && metadataByAssemblyGroupIds[uuid]) {
          partMetadata = metadataByAssemblyGroupIds[uuid]
        }
        return {
          partName: part,
          quantity: part.quantity,
          id: i,
          ...partMetadata,
        }
      }),
    [partsInOperation, metadataByAssemblyGroupIds],
  )

  const [selectedRow, setSelectedRow] = useState<number | null>(null)
  const [selectedColumn, setSelectedColumn] = useState<string | null>(null)

  const [tableData, setTableData] = useState(() => [...initialTableData])

  const partsCustomColumns = useMemo(
    () => template?.template_values.columns || [],
    [template],
  )
  const rawPartsCustomRows = useMemo(
    () => template?.template_values.rows,
    [template],
  )
  const partsCustomRows = useMemo(
    () => rawPartsCustomRows || [],
    [rawPartsCustomRows],
  )

  const tablePosition = useMemo(
    () => template?.template_values.position,
    [template?.template_values.position],
  )
  const columnsSize = useMemo(
    () => template?.template_values.columnsSize,
    [template?.template_values.columnsSize],
  )

  const getEmptyCustomRow = useCallback(
    (markAsPlaceholder: boolean) => {
      const keysFromTemplate = (partsCustomColumns || [])
        .flatMap((c) => Object.keys(c || {}))
        .filter(Boolean)

      const baseKeys = ['id', 'partName', 'quantity']
      const uniqueKeys = Array.from(
        new Set([...(keysFromTemplate || []), ...baseKeys]),
      )

      const row = uniqueKeys.reduce((acc, key) => {
        ;(acc as any)[key] = ''
        return acc
      }, {} as any)

      if (markAsPlaceholder) {
        ;(row as any)[EMPTY_CUSTOM_ROW_KEY] = true
      }

      return row as PartsTableData
    },
    [partsCustomColumns],
  )

  const isPlaceholderEmpty = useCallback((row: any) => {
    if (!row?.[EMPTY_CUSTOM_ROW_KEY]) return false

    return Object.keys(row).every((k) => {
      if (k === EMPTY_CUSTOM_ROW_KEY) return true
      const v = row[k]
      return v === '' || v === null || typeof v === 'undefined'
    })
  }, [])

  useEffect(() => {
    if (initialTableData.length === 0) {
      setTableData([getEmptyCustomRow(true)])
      return
    }

    setTableData(initialTableData)
  }, [initialTableData, getEmptyCustomRow])

  const { mutateAsync: updateTable } = useUpdateTable()
  const { mutateAsync: createTable } = useCreateTable()

  const saveTableData = useCallback(
    async ({
      columns,
      rows,
    }: {
      columns?: Array<TableColumn>
      rows?: Array<TableRow>
    }) => {
      if (template?.id) {
        const tableData: Table = {
          type: dataType,
        }

        if (columns) {
          tableData.columns = columns
        }

        if (rows) {
          tableData.rows = rows
        }

        await updateTable({
          templateAttributeId: template?.id,
          documentPageId: documentPage.id as string,
          tableData,
        })
        return
      }
      await createTable({
        documentPageId: documentPage.id as string,
        tableData: {
          type: dataType,
          columns,
          rows,
        },
      })
    },
    [documentPage.id, dataType, template?.id, updateTable, createTable],
  )

  useEffect(() => {
    if (partsCustomColumns?.length) {
      setColumnsData((prev) => ({
        ...prev,
        ...partsCustomColumns.reduce(
          (accum, customColumn) => ({
            ...accum,
            ...customColumn,
          }),
          {},
        ),
      }))
    }
  }, [partsCustomColumns])

  useEffect(() => {
    if (partsCustomColumns?.length) {
      partsCustomColumns.forEach((column) => {
        Object.keys(column).forEach((accessorKey) => {
          const columnDef =
            initialColumns[accessorKey] ??
            (createNewColumn({
              accessorKey,
              onCellChange,
              selectTable,
              isReadOnly: isReadOnly || metadataKeys.includes(accessorKey),
              documentPageId: documentPage.id as string,
              tableType: 'parts-table',
            }) as ColumnDef<PartsTableData>)
          setColumnsDef((prev) => [...prev, columnDef])
        })
      })
    }

    /* This is needed because the app is running in strict mode so it runs this hook twice */
    return () => {
      setColumnsDef([])
    }
  }, [partsCustomColumns, metadataKeys])

  useEffect(() => {
    let customColumnsData: Array<TableRow> = []
    let customRows = partsCustomRows

    if (partsCustomColumns?.length) {
      customColumnsData = partsCustomRows.slice(0, initialTableData.length)
    }

    if (customColumnsData?.length) {
      customRows = partsCustomRows.slice(
        initialTableData.length,
        partsCustomRows.length,
      )
    }

    setTableData((prev) => {
      const hadPlaceholder = prev.some((r: any) => r?.[EMPTY_CUSTOM_ROW_KEY])
      const prevWithoutPlaceholder = prev.filter(
        (r: any) => !r?.[EMPTY_CUSTOM_ROW_KEY],
      )
      const prevCustomRows = prevWithoutPlaceholder.slice(
        initialTableData.length,
      )

      const mergedDefaultRows = initialTableData.map((row, i) => {
        const cleanRow = Object.keys(customColumnsData[i] || {}).reduce(
          (accum, columnKey) => {
            const isMetadataColumn = metadataKeys?.includes(columnKey)
            if (!isMetadataColumn) {
              accum[columnKey] = (customColumnsData[i] as any)[columnKey]
            }
            return accum
          },
          {} as any,
        )

        if (customColumnsData?.length) {
          return {
            ...row,
            ...(cleanRow as any),
          }
        }

        return row
      })

      const hasRowsFromTemplate = Array.isArray(rawPartsCustomRows)
      const nextCustomRows = hasRowsFromTemplate
        ? customRows || []
        : prevCustomRows
      const next = [...mergedDefaultRows, ...nextCustomRows]

      if (next.length === 0) {
        return [getEmptyCustomRow(true)]
      }

      if (hadPlaceholder) {
        return next.filter((r: any) => !isPlaceholderEmpty(r))
      }

      return next
    })
  }, [
    partsCustomRows,
    partsCustomColumns,
    initialTableData,
    metadataKeys,
    rawPartsCustomRows,
    getEmptyCustomRow,
    isPlaceholderEmpty,
  ])

  const onCellChange = useCallback(
    (rowIndex, columnId, value) => {
      setTableData((prev) =>
        prev.map((row: any, index) => {
          if (index !== rowIndex) return row
          const nextRow = { ...row, [columnId]: value }
          if (nextRow?.[EMPTY_CUSTOM_ROW_KEY] && value !== '') {
            delete nextRow[EMPTY_CUSTOM_ROW_KEY]
          }
          return nextRow
        }),
      )
    },
    [setTableData],
  )

  const onHeaderChange = useCallback((value: string, accessorKey: string) => {
    setColumnsData((prev) => ({
      ...prev,
      [accessorKey]: value,
    }))
  }, [])

  const savePartsTableData = useCallback(async () => {
    const cleanColumns = columnsDef
      // .slice(initialColumns.length, columnsDef.length) Check this
      .reduce((accum, colDef: any) => {
        const columnAccessor = Object.keys(columnsData).find(
          (accessorKey) => accessorKey === colDef.accessorKey,
        )

        if (!columnAccessor) return accum

        const column = columnsData[columnAccessor]

        return [
          ...accum,
          {
            [columnAccessor]: column,
          },
        ]
      }, [] as Array<any>)

    let filteredTableData = tableData
      .slice(0, initialTableData.length)
      .map((row) =>
        Object.keys(row).reduce((accum, key) => {
          if (
            Object.keys(columnsData).includes(key) &&
            !['id', 'partName', 'quantity'].includes(key) &&
            !metadataKeys.includes(key)
          ) {
            accum[key] = row[key]
          }
          return accum
        }, {} as any),
      )

    filteredTableData = [
      ...filteredTableData,
      ...tableData.slice(initialTableData.length, tableData.length),
    ]

    filteredTableData = filteredTableData.map((row: any) => {
      if (!row) return row
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { [EMPTY_CUSTOM_ROW_KEY]: _ignored, ...rest } = row
      return rest
    })

    await saveTableData({
      columns: cleanColumns,
      rows: filteredTableData,
    })
  }, [
    tableData,
    columnsData,
    columnsDef,
    initialTableData.length,
    metadataKeys,
    saveTableData,
  ])

  const addNewColumn = useCallback(
    async ({
      accessorKey,
      newColumn,
      position,
    }: {
      accessorKey: string
      newColumn: ColumnDef<any>
      position: 'left' | 'right'
    }) => {
      const defaultColumns = ['id', 'partName', 'quantity']
      const newColumnAccessor = (newColumn as { accessorKey: string })
        .accessorKey
      const targetIndex = columnsDef.findIndex(
        (column) => (column as any).accessorKey === accessorKey,
      )
      const isMetadataColumn = metadataKeys.includes(newColumnAccessor)
      const isBaseColumn = defaultColumns.includes(newColumnAccessor)
      const shouldReplaceColumn = isMetadataColumn || isBaseColumn
      const insertIndex = position === 'left' ? targetIndex : targetIndex + 1

      let newColumnsDef
      if (shouldReplaceColumn) {
        newColumnsDef = [
          ...columnsDef.slice(0, targetIndex),
          { accessorKey: newColumnAccessor },
          ...columnsDef.slice(targetIndex + 1),
        ]
      } else {
        newColumnsDef = [
          ...columnsDef.slice(0, insertIndex),
          { accessorKey: newColumnAccessor },
          ...columnsDef.slice(insertIndex),
        ]
      }

      const newColumns = newColumnsDef.map((columnDef: any) => {
        const key = columnDef.accessorKey
        const isMetadataKey = metadataKeys.includes(key)
        const headerValue = isMetadataKey
          ? key
          : (columnsData[key] ?? columnOptions[key])
        return { [key]: headerValue ?? '' }
      })

      const updatedRows = [
        ...tableData.slice(0, initialTableData.length).map((row) => {
          const rowKeys = Object.keys(row).filter((key) =>
            key.includes('custom-column'),
          )

          return rowKeys.reduce((accum, key) => {
            if (shouldReplaceColumn && key === accessorKey) return accum
            accum[key] = row[key]
            return accum
          }, {} as any)
        }),
        ...tableData.slice(initialTableData.length).map((row) => {
          const seededValue = (row as any)[newColumnAccessor] ?? ''
          return {
            ...row,
            [newColumnAccessor]: shouldReplaceColumn ? seededValue : '',
          }
        }),
      ]

      await saveTableData({
        columns: newColumns,
        rows: updatedRows,
      })
    },
    [
      columnsDef,
      columnsData,
      tableData,
      metadataKeys,
      columnOptions,
      initialTableData.length,
      saveTableData,
    ],
  )

  const removeColumn = useCallback(
    async (accessorKey: string) => {
      const columnBefore = columnsDef.findIndex(
        (column) => (column as any).accessorKey === accessorKey,
      )

      if (columnBefore === -1) return
      setColumnsDef((prevColumns) => [
        ...prevColumns.slice(0, columnBefore),
        ...prevColumns.slice(columnBefore + 1),
      ])
      setTableData((prevTableData) =>
        prevTableData.filter((row) => Object.keys(row).includes(accessorKey)),
      )
      setColumnsData((prev) => {
        const newColumns = { ...prev }
        delete newColumns[accessorKey]
        return newColumns
      })

      const filteredRows = partsCustomRows.map((row) => {
        return Object.keys(row).reduce((acc, currAccessorKey) => {
          if ((currAccessorKey as string) !== accessorKey) {
            acc[currAccessorKey] = row[currAccessorKey]
          }

          return acc
        }, {})
      })

      const filteredColumns = columnsDef
        .filter(
          (column) => ((column as any).accessorKey as string) !== accessorKey,
        )
        .map((column) => {
          const accessorKey = (column as any).accessorKey
          return { [accessorKey]: columnsData[accessorKey] }
        })

      await saveTableData({
        columns: filteredColumns,
        rows: filteredRows,
      })
    },
    [columnsDef, columnsData, partsCustomRows, setColumnsDef, saveTableData],
  )

  const removeRow = useCallback(
    async (rowIndex) => {
      const row = tableData[rowIndex]
      if (row?.[EMPTY_CUSTOM_ROW_KEY]) {
        setTableData((prev) => prev.filter((_, i) => i !== rowIndex))
        return
      }
      let offset = initialTableData.length

      if (partsCustomColumns?.length) {
        offset = 0
      }

      const filteredRows = partsCustomRows.filter(
        (_, index) => index !== rowIndex - offset,
      )

      setTableData((prev) => prev.filter((_, index) => index !== rowIndex))
      await saveTableData({
        rows: filteredRows,
        columns: partsCustomColumns,
      })
    },
    [
      partsCustomRows,
      partsCustomColumns,
      tableData,
      initialTableData.length,
      setTableData,
      saveTableData,
    ],
  )

  const addNewRow = useCallback(
    async ({
      rowIndex,
      newRow,
      position,
    }: {
      rowIndex?: number
      newRow: any
      position: 'above' | 'below'
    }) => {
      let insertIndex: number

      if (typeof rowIndex === 'number') {
        insertIndex = position === 'above' ? rowIndex : rowIndex + 1
      } else {
        insertIndex = position === 'above' ? 0 : tableData.length
      }

      const updatedRows = [
        ...tableData.slice(0, insertIndex),
        newRow,
        ...tableData.slice(insertIndex),
      ]

      const cleanedUpdatedRows = updatedRows.map((r: any) => {
        if (!r) return r
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { [EMPTY_CUSTOM_ROW_KEY]: _ignored, ...rest } = r
        return rest
      })

      setTableData(cleanedUpdatedRows)

      let filteredTableData = cleanedUpdatedRows
        .slice(0, initialTableData.length)
        .map((row) =>
          Object.keys(row).reduce((accum, key) => {
            const isMetadataKey = metadataKeys.includes(key)
            const isBaseKey = ['id', 'partName', 'quantity'].includes(key)
            if (!(isMetadataKey || isBaseKey)) {
              accum[key] = row[key]
            }
            return accum
          }, {} as any),
        )

      filteredTableData = [
        ...filteredTableData,
        ...cleanedUpdatedRows.slice(
          initialTableData.length,
          updatedRows.length,
        ),
      ]

      await saveTableData({
        rows: filteredTableData,
      })
    },
    [saveTableData, tableData, metadataKeys, initialTableData.length],
  )

  const partNameSuggestions = useMemo(
    // Keep suggestions stable across renders to avoid re-filtering churn in cells.
    () => fullBom.map((part) => part.partName).filter(Boolean),
    [fullBom],
  )
  const tableSuggestions = useMemo(
    () => ({
      partName: partNameSuggestions,
    }),
    [partNameSuggestions],
  )

  return (
    <EditableTable
      suggestions={tableSuggestions}
      headerSuggestions={columnOptions}
      saveTableData={savePartsTableData}
      onCellChange={onCellChange}
      removeRow={removeRow}
      addRow={addNewRow}
      onHeaderChange={onHeaderChange}
      saveCustomColumns={savePartsTableData}
      removeColumn={removeColumn}
      addColumn={addNewColumn}
      setSelectedColumn={setSelectedColumn}
      setSelectedRow={setSelectedRow}
      selectedRow={selectedRow}
      selectedColumn={selectedColumn}
      documentPage={documentPage}
      tableData={tableData}
      defaultRows={initialTableData.length}
      columnsDef={columnsDef as any}
      columnsData={columnsData}
      positionData={tablePosition}
      columnsSize={columnsSize || {}}
      tableTemplate={template as any}
      id={draggableId}
      type={dataType}
      isReadOnly={isReadOnly}
      notCustomColumns={['id']}
      testId="parts-table"
      onNudgeEnd={onNudgeEnd}
      onNudgeStart={onNudgeStart}
    />
  )
}

type NameCellProps = {
  getValue: CellContext<PartsTableData, unknown>['getValue']
  selectTable: (tableId: string) => void
  tableId: string
  tableMeta?: EditableTableMeta
  documentPageId: string
  width: number
  row: RowType<PartsTableData>
  column: ColumnType<PartsTableData>
}
const NameCell = ({
  getValue,
  selectTable,
  tableId,
  tableMeta,
  documentPageId,
  width,
  row,
  column,
}: NameCellProps) => {
  const {
    data: { assemblyTree },
  } = useAssemblyTreeQuery()
  const { toast } = useToast()
  const [hasChanged, setHasChanged] = useState(false)
  const part = getValue<any>()
  const isString = useMemo(() => typeof part === 'string', [part])
  const accessor = (column.columnDef as any).accessorKey as string
  const rowIndex = row.index
  const initialPartName = isString ? part : part?.partName
  const [partName, setPartName] = useState(initialPartName || '')

  useEffect(() => {
    setPartName(initialPartName || '')
  }, [initialPartName])

  const { mutate: updateCadVersion } = useUpdateCadVersionMutation()
  const { data: cadData, refetchCadVersion } = useCADQuery()

  const cadVersionId = useMemo(
    () => cadData?.version?.id as string,
    [cadData?.version],
  )
  const documentVersionId = useMemo(
    () => cadData?.documentVersion?.id as string,
    [cadData?.documentVersion],
  )
  const handleOnChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
    setHasChanged(true)
    const updatedValue = event.target.value
    setPartName(updatedValue)
  }

  const handlePartNameChange = useCallback(async () => {
    if (isString || !assemblyTree) return
    if (!partName) {
      setPartName(initialPartName)
      toast({
        title: `Can't rename part ${initialPartName}`,
        description: 'Part name cannot be empty',
        variant: 'destructive',
      })
      return
    }
    const tree = { ...assemblyTree }
    const UUIDs = part.UUIDs
    UUIDs.forEach((uuid) => {
      const node = tree.nodes?.find((node) => node.uuid === uuid)
      if (node) {
        node.display_name = partName
      }
    })
    if (assemblyTree && cadVersionId && documentVersionId) {
      await updateCadVersion({
        cadVersionId,
        documentVersionId,
        assemblyTree: tree,
      })
      refetchCadVersion()
    }
  }, [
    isString,
    assemblyTree,
    partName,
    part.UUIDs,
    cadVersionId,
    documentVersionId,
    initialPartName,
    toast,
    updateCadVersion,
    refetchCadVersion,
  ])

  const textareaRef = useRef<HTMLTextAreaElement>(null)

  const adjustHeight = useCallback(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto'
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`
    }
  }, [textareaRef])

  useEffect(() => {
    const observer = new ResizeObserver((entries) => {
      if (entries && entries.length) {
        adjustHeight()
      }
    })

    if (textareaRef.current) {
      observer.observe(textareaRef.current)
    } else {
      observer.disconnect()
    }

    return () => {
      observer.disconnect()
    }
  }, [textareaRef, adjustHeight])

  useEffect(() => {
    adjustHeight()
  }, [partName, adjustHeight])

  const handleOnBlur = useCallback(async () => {
    tableMeta?.clearHighlightedCell('focus', {
      columnId: accessor,
      rowIndex,
    })
    if (hasChanged) {
      await handlePartNameChange()
      setHasChanged(false)
    }
  }, [accessor, hasChanged, handlePartNameChange, rowIndex, tableMeta])

  const handleOnFocus = useCallback(() => {
    tableMeta?.highlightCell({
      columnId: accessor,
      rowIndex,
      source: 'focus',
    })
  }, [accessor, rowIndex, tableMeta])

  return (
    <TableCell
      id={`part-${documentPageId}-${initialPartName.replace(/\s/g, '-')}`}
      className="border-x border-solid border-gray-200 border-collapse py-0 bg-white/70"
      style={{
        position: 'relative',
        width,
        boxShadow: getHighlightedCellStyle({
          meta: tableMeta,
          rowIndex,
          columnId: accessor,
        }),
      }}
      data-testid="parts-table__partName-cell"
      data-id-column={accessor}
      data-id-row={rowIndex}
    >
      <Textarea
        className="border-none shadow-none m-0 px-0 py-1 text-xs min-h-4 overflow-hidden resize-none shadow-none"
        style={{
          outline: 'none',
          boxShadow: 'none',
        }}
        value={partName}
        onChange={handleOnChange}
        onFocus={handleOnFocus}
        onBlur={handleOnBlur}
        onMouseDownCapture={(e) => e.stopPropagation()}
        onClick={(e) => {
          e.stopPropagation()
          selectTable(tableId)
        }}
        data-testid="parts-table-cell-input"
        ref={textareaRef}
        data-id-column={accessor}
        data-id-row={rowIndex}
        rows={1}
      />
    </TableCell>
  )
}
